import React, { FC, useState } from 'react';
import { DeleteButton } from '../../../shared/globals';
import { T } from '../../../shared/helpers';
import { FormBuilderItem } from './index';
import styled from 'styled-components';
import update from 'immutability-helper';
import { Checkbox } from '../checkbox';
import { FormControl } from '../formControl';
import { Icon } from '../icon';
import { Input } from '../input';
import { Select } from '../select';
import { TextArea } from '../textArea';

export interface Detail {
    pos?: number | undefined;
    value?: string | undefined;
    label?: string | undefined;
}

const EditFormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow-y: auto;
`;

export const LabelInput = styled(Input)`
    width: 100%;
    &:focus,
    &:active {
        border: ${(props) => props.theme.app.primary} 1px solid !important;
    }

    &:hover {
        border: 1px solid rgb(221, 221, 221);
    }
`;

export const ValueInput = styled(Input)`
    width: 100%;
    &:focus,
    &:active {
        border: ${(props) => props.theme.app.primary} 1px solid !important;
    }

    &:hover {
        border: 1px solid rgb(221, 221, 221);
    }
`;

const ItemContainer = styled.div<{ $xs: number }>`
    display: grid;
    grid-template-columns: 2fr 1fr 24px 24px;
    gap: 8px;
    align-items: start;

    ${Icon} {
        margin-top: 9px !important;
    }
`;

const AddOptionIcon = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: 1px solid #dbe2e6;
    border-radius: 4px;
    background: #fff;
    font-size: 16px;
    font-weight: 600;
    color: #999;
    cursor: pointer;
    margin-top: 9px;
    flex-shrink: 0;

    &:hover {
        border-color: ${(props) => props.theme.app.primary};
        color: ${(props) => props.theme.app.primary};
    }
`;

const EditContent = styled(TextArea)`
    width: 100% !important;
    height: 200px !important;
`;

const TwoColRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
`;

const EditForm: FC<{
    item: FormBuilderItem;
    onChange: (result: FormBuilderItem) => void;
}> = (props) => {
    const [editableItem, setEditableItem] = useState(props.item || null);
    const [details, setDetails] = useState<Detail[]>((props.item.options as Detail[]) || ([{ pos: 1, value: '', label: '' }] as Detail[]));
    const [placeholder, setPlaceholder] = useState(props.item?.placeholder || '');
    const isEmailField = editableItem.type === 'email' && editableItem.name === 'field_email';

    const handleChange = (label: string, value: string | boolean) => {
        setEditableItem((prev) => {
            const getPreviuos = prev;

            if ((label == 'label' && getPreviuos.placeholder == '') || (label == 'label' && getPreviuos.placeholder === getPreviuos.label)) {
                prev = update(prev, {
                    ['placeholder']: { $set: value.toString() },
                    ['label']: { $set: value.toString() }
                });
                setPlaceholder((prev) => {
                    prev = value.toString();
                    return prev;
                });
            } else {
                if (label == 'placeholder') {
                    setPlaceholder((prev) => {
                        prev = value.toString();
                        return prev;
                    });
                }
                prev = update(prev, { [label]: { $set: value } });
            }
            props.onChange(prev);
            return prev;
        });
    };

    const handleChangeOption = (item: Detail, label: string, value: string) => {
        if (label == 'label') {
            item.label = value;
        } else {
            item.value = value;
        }

        const index = details?.findIndex((x) => x.pos == item.pos);

        if (index !== -1) {
            const options = update(details, { [index]: { $set: item } });
            setDetails((prev) => {
                prev = options;
                return prev;
            });
            if (details.length == 1 && label === 'value') {
                setEditableItem((prev) => {
                    prev = update(prev, {
                        ['options']: { $set: options },
                        ['value']: { $set: value }
                    });
                    props.onChange(prev);
                    return prev;
                });
            } else {
                setEditableItem({ ...editableItem, ['options']: options });
                props.onChange(editableItem);
            }
        }
    };

    const handleRemoveOption = (item: Detail) => {
        const index = details?.findIndex((x) => x.pos == item.pos);

        if (index != undefined && index !== -1) {
            const options = details?.splice(index, 1);
            setEditableItem((prev) => {
                prev = update(prev, { ['options']: { $set: options } });
                props.onChange(editableItem);
                return prev;
            });
        }
    };

    const handleAddField = () => {
        const item = details?.findLast((x) => x.pos);
        if (item) {
            const idx = item?.pos ? item?.pos + 1 : details.length + 1;
            const detail = { pos: idx, label: '', value: '' };
            const options = update(details, { $push: [detail] });
            setDetails((prev) => {
                prev = options;
                return prev;
            });
            setEditableItem((prev) => {
                prev = update(prev, { ['options']: { $set: options } });
                props.onChange(prev);
                return prev;
            });
        }
    };

    return (
        <EditFormContainer>
            {/* Label + Required */}
            {editableItem.hasOwnProperty('label') && (
                <FormControl label={T._('Label', 'Admin')}>
                    <Input
                        id='label'
                        name='label'
                        type='text'
                        className='form-control'
                        defaultValue={editableItem.label}
                        onBlur={(e) => handleChange('label', e.currentTarget.value)}
                    />
                    <Checkbox
                        disabled={isEmailField}
                        label={T._('Required', 'Admin')}
                        checked={editableItem.required || false}
                        onChange={(checked) => handleChange('required', checked)}
                    />
                </FormControl>
            )}

            {/* Header text */}
            {!isEmailField && editableItem.hasOwnProperty('headertext') && (
                <FormControl label={T._('Header text', 'Admin')}>
                    <Input
                        id='headertext'
                        name='headertext'
                        type='text'
                        className='form-control'
                        defaultValue={editableItem.headertext}
                        onBlur={(e) => handleChange('headertext', e.currentTarget.value)}
                    />
                </FormControl>
            )}

            {/* Header level */}
            {!isEmailField && editableItem.hasOwnProperty('headerlevel') && (
                <FormControl label={T._('Header level', 'Admin')}>
                    <Select
                        key={'headerlevel'}
                        name={'headerlevel'}
                        value={editableItem.headerlevel ?? 'h1'}
                        className='form-control'
                        onChange={(e) => handleChange('headerlevel', e)}
                        onBlur={(e) => handleChange('headerlevel', e)}
                    >
                        <option value='h1'>{T._('H1', 'Admin')}</option>
                        <option value='h2'>{T._('H2', 'Admin')}</option>
                        <option value='h3'>{T._('H3', 'Admin')}</option>
                        <option value='h4'>{T._('H4', 'Admin')}</option>
                        <option value='h5'>{T._('H5', 'Admin')}</option>
                        <option value='h6'>{T._('H6', 'Admin')}</option>
                    </Select>
                </FormControl>
            )}

            {/* Paragraph content */}
            {!isEmailField && editableItem.hasOwnProperty('content') && (
                <FormControl label={T._('Content', 'Admin')}>
                    <EditContent
                        id='content'
                        name='content'
                        className='form-control'
                        defaultValue={editableItem.content}
                        onBlur={(e) => handleChange('content', e.currentTarget.value)}
                    />
                </FormControl>
            )}

            {/* Options (radio / select) */}
            {!isEmailField && editableItem.hasOwnProperty('options') && editableItem?.options != undefined && editableItem?.options?.length > 0 && (
                <div>
                    {details?.map((x, idx) => (
                        <ItemContainer key={x.pos} $xs={3}>
                            <FormControl>
                                <LabelInput
                                    readOnly={editableItem?.readOnly}
                                    id={'props_element_' + x.pos + '_label'}
                                    type='text'
                                    name='label'
                                    placeholder={T._('Label', 'Admin')}
                                    className='form-control'
                                    defaultValue={x.label || ''}
                                    onBlur={(e) => handleChangeOption(x, 'label', e.currentTarget.value)}
                                />
                            </FormControl>
                            <FormControl>
                                <ValueInput
                                    readOnly={false}
                                    id={'props_element_' + x.pos + '_value'}
                                    name='value'
                                    placeholder={T._('Value', 'Admin')}
                                    className='form-control'
                                    defaultValue={x.value || ''}
                                    onBlur={(e) => handleChangeOption(x, 'value', e.currentTarget.value)}
                                />
                            </FormControl>
                            {x?.pos && x?.pos > 1 && !editableItem?.readOnly ? <DeleteButton onClick={() => handleRemoveOption(x)} /> : <div />}
                            {!editableItem.readOnly && idx === details.length - 1 ? <AddOptionIcon onClick={() => handleAddField()}>+</AddOptionIcon> : <div />}
                        </ItemContainer>
                    ))}
                </div>
            )}

            {/* Type selector (text fields) */}
            {!isEmailField && editableItem.hasOwnProperty('type') && (
                <FormControl label={T._('Type', 'Admin')}>
                    <Select
                        key={'type'}
                        name={'type'}
                        className='form-control'
                        value={editableItem.type}
                        onChange={(e) => handleChange('type', e)}
                        onBlur={(e) => handleChange('type', e)}
                    >
                        <option value='text'>{T._('text', 'Admin')}</option>
                        <option value='email'>{T._('email', 'Admin')}</option>
                        <option value='password'>{T._('password', 'Admin')}</option>
                        <option value='tel'>{T._('tel', 'Admin')}</option>
                        <option value='url'>{T._('url', 'Admin')}</option>
                        <option value='date'>{T._('date', 'Admin')}</option>
                    </Select>
                </FormControl>
            )}

            {/* Placeholder + Title side by side */}
            {editableItem.hasOwnProperty('placeholder') && editableItem.hasOwnProperty('tipText') && (
                <TwoColRow>
                    <FormControl label={T._('Placeholder', 'Admin')}>
                        <Input
                            id='placeholder'
                            name='placeholder'
                            type='text'
                            className='form-control'
                            defaultValue={placeholder}
                            value={placeholder}
                            onChange={(e) => setPlaceholder(e.currentTarget.value)}
                            onBlur={(e) => handleChange('placeholder', e.currentTarget.value)}
                        />
                    </FormControl>
                    {!isEmailField && (
                        <FormControl label={T._('Title', 'Admin')}>
                            <Input
                                id='tipText'
                                name='tipText'
                                type='text'
                                className='form-control'
                                defaultValue={editableItem.tipText}
                                onBlur={(e) => handleChange('tipText', e.currentTarget.value)}
                            />
                        </FormControl>
                    )}
                </TwoColRow>
            )}

            {/* Placeholder only (no tipText) */}
            {editableItem.hasOwnProperty('placeholder') && !editableItem.hasOwnProperty('tipText') && (
                <FormControl label={T._('Placeholder', 'Admin')}>
                    <Input
                        id='placeholder'
                        name='placeholder'
                        type='text'
                        className='form-control'
                        defaultValue={placeholder}
                        value={placeholder}
                        onChange={(e) => setPlaceholder(e.currentTarget.value)}
                        onBlur={(e) => handleChange('placeholder', e.currentTarget.value)}
                    />
                </FormControl>
            )}

            {/* Title only (no placeholder) */}
            {!isEmailField && editableItem.hasOwnProperty('tipText') && !editableItem.hasOwnProperty('placeholder') && (
                <FormControl label={T._('Title', 'Admin')}>
                    <Input
                        id='tipText'
                        name='tipText'
                        type='text'
                        className='form-control'
                        defaultValue={editableItem.tipText}
                        onBlur={(e) => handleChange('tipText', e.currentTarget.value)}
                    />
                </FormControl>
            )}

            {/* Default value */}
            {!isEmailField && editableItem.hasOwnProperty('value') && typeof editableItem.value === 'string' && (
                <FormControl label={T._('Default value', 'Admin')}>
                    <Input
                        id='value'
                        name='value'
                        type='text'
                        className='form-control'
                        defaultValue={editableItem?.value?.toString() ?? ''}
                        onBlur={(e) => handleChange('value', e.currentTarget.value)}
                    />
                </FormControl>
            )}

            {/* Default selected (checkbox) */}
            {!isEmailField && editableItem.hasOwnProperty('defaultSelected') && (
                <FormControl>
                    <Checkbox
                        label={T._('Default selected', 'Admin')}
                        checked={editableItem.defaultSelected || false}
                        onChange={(checked) => handleChange('defaultSelected', checked)}
                    />
                </FormControl>
            )}

            {/* Min/Max length side by side */}
            {editableItem?.type !== 'email' && editableItem.hasOwnProperty('min_length') && editableItem.hasOwnProperty('max_length') && (
                <TwoColRow>
                    <FormControl label={T._('Min length', 'Admin')}>
                        <Input
                            id='min_length'
                            step='1'
                            type='number'
                            name='min_length'
                            className='form-control'
                            defaultValue={editableItem?.min_length}
                            onBlur={(e) => handleChange('min_length', e.currentTarget.value)}
                        />
                    </FormControl>
                    <FormControl label={T._('Max length', 'Admin')}>
                        <Input
                            id='max_length'
                            name='max_length'
                            step='1'
                            type='number'
                            className='form-control'
                            defaultValue={editableItem.max_length}
                            onBlur={(e) => handleChange('max_length', e.currentTarget.value)}
                        />
                    </FormControl>
                </TwoColRow>
            )}

            {/* Min/Max value side by side (number fields) */}
            {editableItem?.type !== 'email' && editableItem.hasOwnProperty('min_value') && editableItem.hasOwnProperty('max_value') && (
                <TwoColRow>
                    <FormControl label={T._('Min value', 'Admin')}>
                        <Input
                            id='min_value'
                            name='min_value'
                            type='number'
                            className='form-control'
                            defaultValue={editableItem.min_value}
                            onBlur={(e) => handleChange('min_value', e.currentTarget.value)}
                        />
                    </FormControl>
                    <FormControl label={T._('Max value', 'Admin')}>
                        <Input
                            id='max_value'
                            name='max_value'
                            step='1'
                            type='number'
                            className='form-control'
                            defaultValue={editableItem.max_value}
                            onBlur={(e) => handleChange('max_value', e.currentTarget.value)}
                        />
                    </FormControl>
                </TwoColRow>
            )}

            {/* Step + Suffix side by side */}
            {editableItem?.type !== 'email' && editableItem.hasOwnProperty('step') && editableItem.hasOwnProperty('sufix') && (
                <TwoColRow>
                    <FormControl label={T._('Step', 'Admin')}>
                        <Input
                            id='step'
                            name='step'
                            type='number'
                            className='form-control'
                            defaultValue={editableItem.step}
                            onBlur={(e) => handleChange('step', e.currentTarget.value)}
                        />
                    </FormControl>
                    <FormControl label={T._('Suffix', 'Admin')}>
                        <Input
                            id='sufix'
                            name='sufix'
                            type='text'
                            className='form-control'
                            defaultValue={editableItem.sufix}
                            onBlur={(e) => handleChange('sufix', e.currentTarget.value)}
                        />
                    </FormControl>
                </TwoColRow>
            )}

            {/* Cols/Rows side by side (textarea) */}
            {editableItem?.type !== 'email' && editableItem.hasOwnProperty('cols') && editableItem.hasOwnProperty('rows') && (
                <TwoColRow>
                    <FormControl label={T._('Cols', 'Admin')}>
                        <Input
                            id='cols'
                            name='cols'
                            step='1'
                            type='number'
                            className='form-control'
                            defaultValue={30}
                            onBlur={(e) => handleChange('cols', e.currentTarget.value)}
                        />
                    </FormControl>
                    <FormControl label={T._('Rows', 'Admin')}>
                        <Input
                            id='rows'
                            name='rows'
                            step='1'
                            type='number'
                            className='form-control'
                            defaultValue={10}
                            onBlur={(e) => handleChange('rows', e.currentTarget.value)}
                        />
                    </FormControl>
                </TwoColRow>
            )}
        </EditFormContainer>
    );
};

export default EditForm;
